import { getPlayerId } from './playerId';

const TOKEN_KEY = 'langshazam.token';
const USER_KEY = 'langshazam.user';
const NEXT_KEY = 'langshazam.auth.next';
const AUTH_EVENT = 'langshazam-auth';

function errorMessage(err, fallback) {
  const detail = err?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}

export const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID
  || '211257978512-c0si63489v5kffrc88vp5kr34rc8n56o.apps.googleusercontent.com';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; }
  catch { return ''; }
}

export function getUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function isSignedIn() {
  return Boolean(getToken() && getUser());
}

export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function setSession(token, user) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch { /* private mode */ }
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function signOut() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* ignore */ }
  window.dispatchEvent(new Event(AUTH_EVENT));
}

const TERMS_KEY = 'langshazam.auth.terms';

export function startGoogleSignIn(nextPath = '/langle', termsVersion = null) {
  if (!GOOGLE_CLIENT_ID) return;
  try {
    sessionStorage.setItem(NEXT_KEY, nextPath);
    // The Terms version the user ticked before leaving for Google; sent
    // with the code exchange so the acceptance is recorded on the account.
    if (termsVersion) sessionStorage.setItem(TERMS_KEY, termsVersion);
    else sessionStorage.removeItem(TERMS_KEY);
  } catch { /* ignore */ }
  const redirectUri = `${window.location.origin}/auth/callback`;
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    prompt: 'select_account',
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export function consumeAuthNext() {
  try {
    const next = sessionStorage.getItem(NEXT_KEY) || '/langle';
    sessionStorage.removeItem(NEXT_KEY);
    return next.startsWith('/') ? next : '/langle';
  } catch {
    return '/langle';
  }
}

export async function exchangeGoogleCode(httpBase, code) {
  const redirectUri = `${window.location.origin}/auth/callback`;
  let termsVersion = null;
  try {
    termsVersion = sessionStorage.getItem(TERMS_KEY);
    sessionStorage.removeItem(TERMS_KEY);
  } catch { /* ignore */ }
  const res = await fetch(`${httpBase}/auth/google/code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      redirectUri,
      playerId: getPlayerId(),
      ...(termsVersion ? { termsVersion } : {}),
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(errorMessage(err, 'Google sign-in failed'));
  }
  const data = await res.json();
  applyAuthPayload(data);
  return data;
}

export function applyAuthPayload(data) {
  setSession(data.accessToken, data.user);
  if (data.stats) {
    try { localStorage.setItem('langle.stats', JSON.stringify(data.stats)); } catch { /* ignore */ }
  }
  return data;
}

export function subscribeAuth(onChange) {
  const handler = () => onChange(getUser());
  window.addEventListener(AUTH_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(AUTH_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}
